import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  ConnectedStatusBadgeText,
  DataGridContainer,
  DataGridItem,
  DataGridLabel,
  DataGridValue,
  DisconnectedStatusBadgeText,
  DuplexStatusBadgeText,
  PortDetailsContainer,
  PortHeader,
  StatusBadge,
} from '../Connections/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { EthernetPort, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { TitleField, ValueField } from '../../Resume/styles';
import { Info } from '../../styles';

function LanDetails({
  selectedPort,
  isConnected,
  isEnabled,
}: {
  selectedPort: LanPortType;
  isConnected: boolean;
  isEnabled: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const portName = selectedPort.name?.value || 'Port';
  const macAddress = selectedPort.macAddress?.value || 'N/A';
  const duplexMode = selectedPort.duplexMode?.value || 'N/A';

  const bytesSent = Number(selectedPort.bytesSent?.value ?? 0);
  const bytesReceived = Number(selectedPort.bytesReceived?.value ?? 0);
  const packetsSent = Number(selectedPort.packetsSent?.value ?? 0);
  const packetsReceived = Number(selectedPort.packetsReceived?.value ?? 0);
  const errorsSent = Number(selectedPort.errorsSent?.value ?? 0);
  const errorsReceived = Number(selectedPort.errorsReceived?.value ?? 0);
  const discardSent = Number(selectedPort.discardPacketsSent?.value ?? 0);
  const discardReceived = Number(
    selectedPort.discardPacketsReceived?.value ?? 0
  );

  return (
    <PortDetailsContainer>
      {/* Header with port information */}
      <PortHeader>
        <Stack direction="row" alignItems="flex-start" gap={1.5}>
          <EthernetPort size={28} color={theme.palette.primary['06']} />
          <Stack>
            <Typography variant="h6" sx={{ fontWeight: 700 }} textAlign="left">
              {String(portName)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.primary, fontWeight: 300 }}
            >
              {String(macAddress).toUpperCase()}
            </Typography>
          </Stack>
        </Stack>

        <Stack>
          <Info sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DynamicIcon name="download" size={32} />
            <TitleField>{t('equipment.wan.bytesReceived')}: </TitleField>
            <ValueField>{bytesReceived}</ValueField>
          </Info>

          <Info sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DynamicIcon name="upload" size={32} />
            <TitleField>{t('equipment.wan.bytesSent')}: </TitleField>
            <ValueField>{bytesSent}</ValueField>
          </Info>
        </Stack>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <StatusBadge status={selectedPort.status?.value} enabled={isEnabled}>
            {isConnected ? (
              <>
                <Wifi size={16} />
                <ConnectedStatusBadgeText
                  variant="caption"
                  color="currentColor"
                >
                  {t(`equipment.status.${selectedPort.status?.value}`)}
                </ConnectedStatusBadgeText>
              </>
            ) : (
              <>
                <WifiOff size={16} color={theme.palette.fixed.white} />
                <DisconnectedStatusBadgeText variant="caption">
                  {t(`equipment.status.${selectedPort.status?.value}`)}
                </DisconnectedStatusBadgeText>
              </>
            )}
          </StatusBadge>
          <StatusBadge status="duplex">
            <DuplexStatusBadgeText variant="caption">
              {String(duplexMode)}
            </DuplexStatusBadgeText>
          </StatusBadge>
        </Stack>
      </PortHeader>

      {/* Statistics Grid */}
      <DataGridContainer>
        <DataGridItem>
          <DataGridLabel>{t('equipment.packetsSent')}</DataGridLabel>
          <DataGridValue>{packetsSent.toLocaleString()}</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.packetsReceived')}</DataGridLabel>
          <DataGridValue>{packetsReceived.toLocaleString()}</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.errorsSent')}</DataGridLabel>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <DataGridValue
              sx={{
                color: errorsSent > 0 ? theme.palette.error.main : 'inherit',
              }}
            >
              {errorsSent}
            </DataGridValue>
            {errorsSent > 0 && (
              <AlertCircle size={14} color={theme.palette.error.main} />
            )}
          </Stack>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.errorsReceived')}</DataGridLabel>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <DataGridValue
              sx={{
                color:
                  errorsReceived > 0 ? theme.palette.error.main : 'inherit',
              }}
            >
              {errorsReceived}
            </DataGridValue>
            {errorsReceived > 0 && (
              <AlertCircle size={14} color={theme.palette.error.main} />
            )}
          </Stack>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.discardedSent')}</DataGridLabel>
          <DataGridValue>{discardSent}</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.discardedReceived')}</DataGridLabel>
          <DataGridValue>{discardReceived}</DataGridValue>
        </DataGridItem>
      </DataGridContainer>
    </PortDetailsContainer>
  );
}

export default LanDetails;
